import { showAlert } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { AuthServices } from '@/features/auth/services';
import { ApiError } from '@/libs/api';
import {
  fetchSignInMethodsForEmail,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from '@/libs/firebase/auth';
import { shouldSuppressErrorAlert } from '@/libs/errors/request-timeout';
import { captureException } from '@/libs/logger';
import { useMutation, UseMutationResult } from '@tanstack/react-query';

export const useCheckMethodsForEmail = (): UseMutationResult<string[], ApiError, string> => {
  return useMutation({
    mutationFn: async email => {
      const methods = await fetchSignInMethodsForEmail(email);
      return methods;
    },
    onError: error => {
      if (shouldSuppressErrorAlert(error)) {
        throw error;
      }
      showAlert({
        title: STRINGS.auth.enterEmail.alertTitle,
        message: STRINGS.auth.enterEmail.alertEmailNotApproved,
      });
      throw error;
    },
  });
};

export const useSignIn = (): UseMutationResult<
  Awaited<ReturnType<typeof signInWithEmailAndPassword>>,
  ApiError,
  auth.SignInRequest
> => {
  return useMutation({
    mutationFn: async ({ email, password }) => {
      return await signInWithEmailAndPassword(email, password);
    },
    onError: error => {
      if (shouldSuppressErrorAlert(error)) {
        captureException(
          {
            error_payload: error,
          },
          { tags: { usk_log_type: 'sign_in_error' } },
        );
        throw error;
      }

      const firebaseError = error as { code?: string; message?: string };

      let message: string = STRINGS.auth.enterPassword.errorSignIn;

      if (
        firebaseError?.code === 'auth/wrong-password' ||
        firebaseError?.code === 'auth/invalid-credential' ||
        firebaseError?.code === 'auth/missing-password'
      ) {
        message = STRINGS.auth.enterPassword.errorPassword;
      }

      showAlert({
        title: STRINGS.auth.enterPassword.noticeTitle,
        message,
      });
      captureException(
        {
          error_payload: error,
        },
        { tags: { usk_log_type: 'sign_in_error' } },
      );
      throw error;
    },
  });
};

export const useSendPasswordResetEmail = (): UseMutationResult<void, ApiError, string> => {
  return useMutation({
    mutationFn: async (email: string) => {
      await sendPasswordResetEmail(email);
    },
    onError: error => {
      if (shouldSuppressErrorAlert(error)) {
        throw error;
      }
      showAlert({
        title: STRINGS.auth.forgotPassword.noticeTitle,
        message: STRINGS.auth.forgotPassword.errorSendResetEmail,
      });
      throw error;
    },
  });
};

export const useLookupUserAccount = (): UseMutationResult<
  auth.LookupUserAccountResponse,
  ApiError,
  string
> => {
  return useMutation({
    mutationFn: async email => {
      return await AuthServices.lookupUserAccount(email);
    },
    onError: error => {
      if (shouldSuppressErrorAlert(error)) {
        throw error;
      }
      showAlert({
        title: STRINGS.auth.enterEmail.noticeTitle,
        message: STRINGS.auth.enterEmail.alertEmailNotVerified,
      });
      throw error;
    },
  });
};
